import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Chart, Report } from '@bi-studio/shared/lib';
import { getChart, getReport, executeReport } from '@bi-studio/shared/apis';
import { RootState } from '../index';

interface ChartState {
    chart: Chart | null;
    report: Report | null;
    chartData: Record<string, unknown>[];
    variableValues: Record<string, string>;
    isLoading: boolean;
    isExecuting: boolean;
    error: string | null;
}

const initialState: ChartState = {
    chart: null,
    report: null,
    chartData: [],
    variableValues: {},
    isLoading: false,
    isExecuting: false,
    error: null,
};

export const fetchChartDetails = createAsyncThunk(
    'chart/fetchDetails',
    async ({ token, chartId }: { token: string; chartId: string }, { rejectWithValue }) => {
        try {
            const chart = await getChart(token, chartId);
            let report = null;
            if (chart.reportId) {
                report = await getReport(token, chart.reportId);
            }
            return { chart, report };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch chart details');
        }
    }
);

export const executeChartData = createAsyncThunk(
    'chart/executeData',
    async (
        { token, reportId, variables }: { token: string; reportId: string; variables: Record<string, string> },
        { rejectWithValue }
    ) => {
        try {
            const result = await executeReport(token, reportId, {
                pagination: { page: 1, pageSize: 0 },
                variables,
            });
            return { rows: result.rows, variables };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to execute chart report');
        }
    }
);

export const chartSlice = createSlice({
    name: 'chart',
    initialState,
    reducers: {
        setVariableValues: (state, action: PayloadAction<Record<string, string>>) => {
            state.variableValues = action.payload;
        },
        resetChartState: (_state) => {
            return initialState;
        },
        setChartError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Details
            .addCase(fetchChartDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchChartDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.chart = action.payload.chart;
                state.report = action.payload.report;
            })
            .addCase(fetchChartDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Execute Data
            .addCase(executeChartData.pending, (state) => {
                state.isExecuting = true;
                state.error = null;
            })
            .addCase(executeChartData.fulfilled, (state, action) => {
                state.isExecuting = false;
                state.chartData = action.payload.rows;
                state.variableValues = action.payload.variables;
            })
            .addCase(executeChartData.rejected, (state, action) => {
                state.isExecuting = false;
                state.error = action.payload as string;
            });
    },
});

export const { setVariableValues, resetChartState, setChartError } = chartSlice.actions;

// Selectors
export const selectChart = (state: RootState) => state.chart.chart;
export const selectReport = (state: RootState) => state.chart.report;
export const selectChartData = (state: RootState) => state.chart.chartData;
export const selectChartLoading = (state: RootState) => state.chart.isLoading;
export const selectChartExecuting = (state: RootState) => state.chart.isExecuting;
export const selectChartError = (state: RootState) => state.chart.error;
export const selectVariableValues = (state: RootState) => state.chart.variableValues;

export default chartSlice.reducer;
